import { z } from 'zod';

/**
 * Quote response contract for GET /quotes/by-shipment/:id. Not invented — derived from
 * nexvia-api-web's real handler (read-only cross-repo reference, per AGENTS.md):
 * src/modules/quote/handlers/list-quotes-by-shipment.handler.js shapes the response (quotes
 * are grouped server-side by driver, no client-side grouping needed), cross-checked against
 * src/modules/quote/entities/quote.entity.js and src/modules/vehicle/entities/vehicle.entity.js
 * for exact nullability, and src/modules/review/repositories/review-repository.js's
 * getDriverReputationBatch for the reputation shape.
 *
 * `amount`/`clientAmount`/vehicle dimension fields are `decimal` columns. Unlike
 * shipment.repository.js's toDomain(), this handler does NOT parseFloat them before
 * responding, and no global pg decimal type-parser is registered in nexvia-api-web (checked) —
 * they arrive as numeric strings, hence `z.coerce.number()` here (matches the `Number(...)`
 * coercion this app's own quotes view already does defensively at every read site).
 */

// SUPERSEDED is a real backend status but is filtered out server-side before this endpoint
// responds (list-quotes-by-shipment.handler.js) — a quote here never actually has it.
export const QuoteStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'CANCELLED']);

export const QuoteVehicleDataSchema = z.object({
  category: z.string(),
  // Independently privacy-gated (`revealPlate`) same as driver identity — null until revealed.
  plate: z.string().nullable(),
  brand: z.string(),
  model: z.string(),
  load: z.coerce.number().nullable(),
  volume: z.coerce.number().nullable(),
  year: z.string().nullable(),
  bodyType: z.string().nullable(),
  hasRamp: z.boolean(),
  hasPlatform: z.boolean(),
  hasCrane: z.boolean(),
  hasStraps: z.boolean(),
  cargoTypes: z.array(z.string()).nullable(),
  length: z.coerce.number().nullable(),
  width: z.coerce.number().nullable(),
  height: z.coerce.number().nullable(),
  seats: z.number().nullish(),
  serviceType: z.string().nullish()
});

export const QuoteSchema = z.object({
  id: z.number(),
  amount: z.coerce.number(),
  clientAmount: z.coerce.number().nullable(),
  currency: z.enum(['UYU', 'USD']),
  estimatedTime: z.number(),
  comments: z.string().nullable(),
  status: QuoteStatusSchema,
  // NEX-1357: false only for a PENDING quote whose carrier cannot quote right now (the API never
  // says why). Defaults to true so an API without the field — and every existing fixture — keeps
  // parsing as before.
  available: z.boolean().default(true),
  createdAt: z.string(),
  expiresAt: z.string(),
  vehicleId: z.number().nullable(),
  // Not a real column on the Quote entity (confirmed by reading quote.entity.js) — likely a
  // repository-query alias, exact nullability unconfirmed. Kept loose on purpose.
  vehicleType: z.string().nullable().optional(),
  vehicleData: QuoteVehicleDataSchema.nullable(),
  proposedDate: z.string().nullable(),
  proposedTimeSlot: z.string().nullable(),
  // NEX-1380: the proposed day as the pickup place's wall clock (the client's list of quotes only).
  proposedDateLocal: z.string().nullish().catch(null),
  // shipmentType/timeSlot/price are read defensively by this app's quotes view
  // (firstQuote.shipmentType, effectiveQuote.timeSlot/.price fallback chains) but are never
  // actually sent by list-quotes-by-shipment.handler.js (confirmed by reading it) — kept
  // optional purely so those pre-existing reads type-check; presence should not be expected.
  shipmentType: z.string().optional(),
  timeSlot: z.string().optional(),
  price: z.coerce.number().optional()
});

export const DriverReputationSchema = z.object({
  completedShipments: z.number(),
  averageRating: z.number().nullable(),
  reviewCount: z.number(),
  isNew: z.boolean()
});

export const QuoteGroupSchema = z.object({
  carrierName: z.string(),
  driverLetter: z.string(),
  driverId: z.number(),
  driverReputation: DriverReputationSchema.nullable(),
  // Pre-sorted ascending by clientAmount||amount server-side — no client-side sort needed.
  quotes: z.array(QuoteSchema),
  // Only present on the group matching shipment.driverId, while the shipment is in a
  // "confirmed" status set — the shipment's OWN driverDataMasked/driverDataAvailableAt
  // (from getShipmentById, not this endpoint) is what useDriverPrivacyRefetch actually reads.
  driverDataMasked: z.boolean().optional(),
  driverDataAvailableAt: z.string().nullable().optional(),
  driverInfo: z
    .object({
      driverId: z.number(),
      name: z.string(),
      phone: z.string(),
      email: z.string()
    })
    .optional()
});

export const QuoteGroupListSchema = z.array(QuoteGroupSchema);

export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;
export type Quote = z.infer<typeof QuoteSchema>;
export type QuoteGroup = z.infer<typeof QuoteGroupSchema>;
